import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { ClientFormData } from '../problems/types'
import { Modal } from '../shared/modal'
import { UserSearchPicker } from './user-search-picker'

interface AddClientModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: ClientFormData) => Promise<unknown>
  /** Pre-fill the form (e.g. promoting a known platform user). */
  initialData?: Partial<ClientFormData>
  /** Override the modal title (e.g. "Promote to Direct Client"). */
  title?: string
}

const initialFormData: ClientFormData = {
  name: '',
  userId: undefined,
  email: '',
  phone: '',
  location: '',
  timeZone: '',
  programmingLanguage: '',
  status: 'active',
}

export function AddClientModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title = 'Add New Client',
}: AddClientModalProps) {
  const [formData, setFormData] = useState<ClientFormData>({
    ...initialFormData,
    ...initialData,
  })
  const [loading, setLoading] = useState(false)

  // Re-seed when (re)opened so a pre-filled promote starts from the current
  // user's details, not stale state from a previous open.
  useEffect(() => {
    if (isOpen) setFormData({ ...initialFormData, ...initialData })
  }, [isOpen, initialData])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) return
    if (!initialData?.userId && !formData.userId) return

    setLoading(true)
    try {
      await onSubmit(formData)
      setFormData(initialFormData)
      onClose()
    } catch (error) {
      console.error('Failed to create client:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-muted-foreground mb-1 block text-sm font-medium">
            Name <span className="text-destructive">*</span>
          </label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Client name"
            required
          />
        </div>

        {!initialData?.userId && (
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Platform User <span className="text-destructive">*</span>
            </label>
            <UserSearchPicker
              selectedUserId={formData.userId ?? null}
              onSelect={(userId) =>
                setFormData((prev) => ({
                  ...prev,
                  userId: userId ?? undefined,
                }))
              }
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Email
            </label>
            <Input
              type="email"
              name="email"
              value={formData.email || ''}
              onChange={handleChange}
              placeholder="email@example.com"
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Phone
            </label>
            <Input
              type="tel"
              name="phone"
              value={formData.phone || ''}
              onChange={handleChange}
              placeholder="+1-555-123-4567"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Location
            </label>
            <Input
              type="text"
              name="location"
              value={formData.location || ''}
              onChange={handleChange}
              placeholder="City, State"
            />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Time Zone
            </label>
            <Input
              type="text"
              name="timeZone"
              value={formData.timeZone || ''}
              onChange={handleChange}
              placeholder="America/New_York"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Programming Language
            </label>
            <select
              name="programmingLanguage"
              value={formData.programmingLanguage || ''}
              onChange={handleChange}
              className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
            >
              <option value="">Select language</option>
              <option value="Python">Python</option>
              <option value="Java">Java</option>
              <option value="JavaScript">JavaScript</option>
              <option value="TypeScript">TypeScript</option>
              <option value="C++">C++</option>
              <option value="Go">Go</option>
              <option value="Rust">Rust</option>
              <option value="C#">C#</option>
            </select>
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-sm font-medium">
              Status
            </label>
            <select
              name="status"
              value={formData.status || 'active'}
              onChange={handleChange}
              className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </div>
        </div>

        <div className="border-border flex justify-end gap-3 border-t pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              loading ||
              !formData.name.trim() ||
              (!initialData?.userId && !formData.userId)
            }
          >
            {loading ? 'Creating...' : 'Create Client'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
